import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AnalyticsEvent =
  // Ativação
  | 'first_login'
  | 'first_diagnosis_started'
  | 'first_diagnosis_completed'
  | 'diagnosis_completed'
  // Engajamento
  | 'feature_used'
  | 'page_view'
  | 'session_start'
  | 'session_end'
  // Alertas
  | 'first_alert_viewed'
  | 'alert_viewed'
  | 'alert_clicked'
  // Conversão
  | 'locked_feature_clicked'
  | 'upgrade_modal_opened'
  | 'upgrade_started'
  | 'upgrade_completed'
  | 'subscription_started'
  // Radar
  | 'radar_viewed'
  | 'radar_news_clicked'
  | 'radar_insight_expanded'
  // Chat/Conselho
  | 'chat_opened'
  | 'chat_message_sent'
  | 'chat_session_started'
  | 'chat_upgrade_suggested'
  | 'chat_upgrade_clicked'
  // Outros
  | 'nps_submitted';

interface EventProperties {
  feature_name?: string;
  page_name?: string;
  time_to_value_seconds?: number;
  plan_tier?: string;
  nps_score?: number;
  segment?: string;
  // Novos campos para tracking específico
  required_plan?: string;
  alert_id?: string;
  alert_type?: string;
  news_id?: string;
  message_count?: number;
  upgrade_source?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface SaaSBenchmark {
  metric_name: string;
  metric_category: string;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_90: number;
  industry_average: number;
  top_performers: number;
  unit: string;
  description: string;
  source: string;
}

export interface UserMetrics {
  time_to_first_value_seconds: number | null;
  activation_date: string | null;
  total_sessions: number;
  total_diagnoses: number;
  features_adopted: string[];
  last_nps_score: number | null;
  retention_week_1: boolean;
  retention_week_4: boolean;
  retention_week_8: boolean;
  current_streak_days: number;
  max_streak_days: number;
}

// In-memory session tracking
let sessionStartTime: number | null = null;
let sessionId: string | null = null;

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export function useProductAnalytics() {
  const { user, subscription } = useAuth();
  const hasInitializedSession = useRef(false);

  // Initialize session on mount
  useEffect(() => {
    if (!sessionStartTime && !hasInitializedSession.current) {
      sessionStartTime = Date.now();
      sessionId = generateSessionId();
      hasInitializedSession.current = true;
    }
  }, []);

  // Track event to database
  const trackEvent = useCallback(async (
    event: AnalyticsEvent,
    properties?: EventProperties
  ) => {
    if (!user?.id) return;

    const eventData = {
      user_id: user.id,
      event_type: event,
      event_name: properties?.feature_name || properties?.page_name || event,
      properties: {
        ...properties,
        plan_tier: subscription.planTier || 'free',
        timestamp: new Date().toISOString(),
      },
      session_id: sessionId,
    };

    console.log('📊 [Analytics] Tracking event:', event, properties);

    try {
      // Insert event to database
      const { error } = await supabase
        .from('product_analytics_events')
        .insert(eventData);

      if (error) {
        console.error('📊 [Analytics] Error saving event:', error);
        // Fallback to localStorage
        saveToLocalStorage(eventData);
      }

      // Update user metrics for specific events
      if (event === 'first_diagnosis_completed') {
        await updateUserMetrics({
          activation_date: new Date().toISOString(),
          time_to_first_value_seconds: sessionStartTime 
            ? Math.round((Date.now() - sessionStartTime) / 1000) 
            : null,
        });
      }

      if (event === 'session_start') {
        await incrementSessionCount();
      }

      if (event === 'feature_used' && properties?.feature_name) {
        await addFeatureAdoption(properties.feature_name);
      }

    } catch (error) {
      console.error('📊 [Analytics] Error:', error);
      saveToLocalStorage(eventData);
    }
  }, [user?.id, subscription.planTier]);

  // Save to localStorage as fallback
  const saveToLocalStorage = (eventData: any) => {
    const existingEvents = JSON.parse(localStorage.getItem('sentinela_analytics') || '[]');
    existingEvents.push(eventData);
    if (existingEvents.length > 100) existingEvents.shift();
    localStorage.setItem('sentinela_analytics', JSON.stringify(existingEvents));
  };

  // Update user metrics
  const updateUserMetrics = useCallback(async (updates: Partial<UserMetrics>) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_product_metrics')
        .upsert({
          user_id: user.id,
          ...updates,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) console.error('📊 [Analytics] Error updating metrics:', error);
    } catch (error) {
      console.error('📊 [Analytics] Error:', error);
    }
  }, [user?.id]);

  // Increment session count
  const incrementSessionCount = useCallback(async () => {
    if (!user?.id) return;

    try {
      // First get current count
      const { data: current } = await supabase
        .from('user_product_metrics')
        .select('total_sessions')
        .eq('user_id', user.id)
        .single();

      const newCount = (current?.total_sessions || 0) + 1;

      await supabase
        .from('user_product_metrics')
        .upsert({
          user_id: user.id,
          total_sessions: newCount,
          last_active_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });
    } catch (error) {
      console.error('📊 [Analytics] Error incrementing sessions:', error);
    }
  }, [user?.id]);

  // Add feature adoption
  const addFeatureAdoption = useCallback(async (featureName: string) => {
    if (!user?.id) return;

    try {
      const { data: current } = await supabase
        .from('user_product_metrics')
        .select('features_adopted')
        .eq('user_id', user.id)
        .single();

      const currentFeatures = current?.features_adopted || [];
      if (!currentFeatures.includes(featureName)) {
        await supabase
          .from('user_product_metrics')
          .upsert({
            user_id: user.id,
            features_adopted: [...currentFeatures, featureName],
          }, {
            onConflict: 'user_id',
          });
      }
    } catch (error) {
      console.error('📊 [Analytics] Error adding feature:', error);
    }
  }, [user?.id]);

  // Track page view
  const trackPageView = useCallback((pageName: string) => {
    trackEvent('page_view', { page_name: pageName });
  }, [trackEvent]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName: string) => {
    trackEvent('feature_used', { feature_name: featureName });
  }, [trackEvent]);

  // Track first diagnosis completion
  const trackActivation = useCallback(() => {
    const hasActivated = localStorage.getItem('sentinela_activated');
    if (!hasActivated) {
      trackEvent('first_diagnosis_completed');
      localStorage.setItem('sentinela_activated', 'true');
      localStorage.setItem('sentinela_activation_date', new Date().toISOString());
    }
  }, [trackEvent]);

  // Track diagnosis completed (every time)
  const trackDiagnosisCompleted = useCallback(() => {
    trackEvent('diagnosis_completed');
    // Also check for first-time activation
    const hasActivated = localStorage.getItem('sentinela_activated');
    if (!hasActivated) {
      trackEvent('first_diagnosis_completed');
      localStorage.setItem('sentinela_activated', 'true');
      localStorage.setItem('sentinela_activation_date', new Date().toISOString());
    }
  }, [trackEvent]);

  // Track alert viewed
  const trackAlertViewed = useCallback((alertId: string, alertType?: string) => {
    const hasViewedFirst = localStorage.getItem('sentinela_first_alert_viewed');
    if (!hasViewedFirst) {
      trackEvent('first_alert_viewed', { alert_id: alertId, alert_type: alertType });
      localStorage.setItem('sentinela_first_alert_viewed', 'true');
    }
    trackEvent('alert_viewed', { alert_id: alertId, alert_type: alertType });
  }, [trackEvent]);

  // Track locked feature clicked
  const trackLockedFeatureClicked = useCallback((featureName: string, requiredPlan: string) => {
    trackEvent('locked_feature_clicked', { 
      feature_name: featureName, 
      required_plan: requiredPlan 
    });
  }, [trackEvent]);

  // Track upgrade modal opened
  const trackUpgradeModalOpened = useCallback((targetPlan: string, source?: string) => {
    trackEvent('upgrade_modal_opened', { 
      plan_tier: targetPlan,
      upgrade_source: source || 'unknown'
    });
  }, [trackEvent]);

  // Track upgrade started (checkout initiated)
  const trackUpgradeStarted = useCallback((targetPlan: string) => {
    trackEvent('upgrade_started', { plan_tier: targetPlan });
  }, [trackEvent]);

  // Track upgrade completed
  const trackUpgradeCompleted = useCallback((planTier: string) => {
    trackEvent('upgrade_completed', { plan_tier: planTier });
    trackEvent('subscription_started', { plan_tier: planTier });
  }, [trackEvent]);

  // Track Radar usage
  const trackRadarUsage = useCallback((action: 'viewed' | 'news_clicked' | 'insight_expanded', newsId?: string) => {
    if (action === 'viewed') {
      trackEvent('radar_viewed');
    } else if (action === 'news_clicked') {
      trackEvent('radar_news_clicked', { news_id: newsId });
    } else if (action === 'insight_expanded') {
      trackEvent('radar_insight_expanded', { news_id: newsId });
    }
  }, [trackEvent]);

  // Track Chat/Conselho usage
  const trackChatUsage = useCallback((action: 'opened' | 'message_sent' | 'session_started', messageCount?: number) => {
    if (action === 'opened') {
      trackEvent('chat_opened');
    } else if (action === 'message_sent') {
      trackEvent('chat_message_sent', { message_count: messageCount });
    } else if (action === 'session_started') {
      trackEvent('chat_session_started');
    }
  }, [trackEvent]);

  // Submit NPS score
  const submitNPS = useCallback(async (score: number, segment?: string) => {
    trackEvent('nps_submitted', { 
      nps_score: score, 
      segment: segment || subscription.planTier || 'free' 
    });

    if (user?.id) {
      await supabase
        .from('user_product_metrics')
        .upsert({
          user_id: user.id,
          last_nps_score: score,
          last_nps_date: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });
    }
  }, [trackEvent, subscription.planTier, user?.id]);

  // Fetch SaaS benchmarks
  const fetchBenchmarks = useCallback(async (): Promise<SaaSBenchmark[]> => {
    try {
      const { data, error } = await supabase
        .from('saas_benchmarks')
        .select('*')
        .order('metric_category');

      if (error) throw error;
      return (data || []) as SaaSBenchmark[];
    } catch (error) {
      console.error('📊 [Analytics] Error fetching benchmarks:', error);
      return [];
    }
  }, []);

  // Fetch user metrics
  const fetchUserMetrics = useCallback(async (): Promise<UserMetrics | null> => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('user_product_metrics')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as UserMetrics | null;
    } catch (error) {
      console.error('📊 [Analytics] Error fetching user metrics:', error);
      return null;
    }
  }, [user?.id]);

  // Get analytics summary (combined local + db)
  const getAnalyticsSummary = useCallback(async () => {
    const userMetrics = await fetchUserMetrics();
    const benchmarks = await fetchBenchmarks();

    // Get local data as fallback
    const localEvents = JSON.parse(localStorage.getItem('sentinela_analytics') || '[]');
    const ttfv = localStorage.getItem('sentinela_ttfv');
    const activationDate = localStorage.getItem('sentinela_activation_date');
    const lastNPS = JSON.parse(localStorage.getItem('sentinela_last_nps') || 'null');

    return {
      // User metrics from DB (or fallback to local)
      timeToFirstValue: userMetrics?.time_to_first_value_seconds || (ttfv ? parseInt(ttfv) : null),
      activationDate: userMetrics?.activation_date || activationDate,
      totalSessions: userMetrics?.total_sessions || 0,
      totalDiagnoses: userMetrics?.total_diagnoses || 0,
      featuresAdopted: userMetrics?.features_adopted || [],
      featureAdoptionCount: userMetrics?.features_adopted?.length || 0,
      lastNPSScore: userMetrics?.last_nps_score || lastNPS?.score || null,
      retentionWeek1: userMetrics?.retention_week_1 || false,
      retentionWeek4: userMetrics?.retention_week_4 || false,
      retentionWeek8: userMetrics?.retention_week_8 || false,
      currentStreakDays: userMetrics?.current_streak_days || 0,
      maxStreakDays: userMetrics?.max_streak_days || 0,
      isActivated: !!(userMetrics?.activation_date || activationDate),
      
      // Benchmarks for comparison
      benchmarks,
      
      // Local fallback data
      totalLocalEvents: localEvents.length,
    };
  }, [fetchUserMetrics, fetchBenchmarks]);

  // Compare metric against benchmark
  const compareWithBenchmark = useCallback((
    metricName: string, 
    userValue: number, 
    benchmarks: SaaSBenchmark[]
  ): { percentile: string; status: 'excellent' | 'good' | 'average' | 'below_average' } | null => {
    const benchmark = benchmarks.find(b => b.metric_name === metricName);
    if (!benchmark) return null;

    // Determine percentile and status
    if (userValue >= benchmark.percentile_90) {
      return { percentile: 'Top 10%', status: 'excellent' };
    } else if (userValue >= benchmark.percentile_75) {
      return { percentile: 'Top 25%', status: 'good' };
    } else if (userValue >= benchmark.percentile_50) {
      return { percentile: 'Top 50%', status: 'average' };
    } else {
      return { percentile: 'Below median', status: 'below_average' };
    }
  }, []);

  return {
    trackEvent,
    trackPageView,
    trackFeatureUsage,
    trackActivation,
    trackDiagnosisCompleted,
    trackAlertViewed,
    trackLockedFeatureClicked,
    trackUpgradeModalOpened,
    trackUpgradeStarted,
    trackUpgradeCompleted,
    trackRadarUsage,
    trackChatUsage,
    submitNPS,
    getAnalyticsSummary,
    fetchBenchmarks,
    fetchUserMetrics,
    compareWithBenchmark,
  };
}
